import posthog from 'posthog-js'
import { isMobileDevice } from './analytics'
import useConsentStore from '@/stores/useConsentStore'

/**
 * Centralized PostHog user and organization identification (FR-05, FR-06, FR-07).
 * Call this whenever user data is loaded (login, SSO callback, session restore).
 * 
 * FR-08: Respects posthogEnabled flag - completely disables all tracking if disabled
 * FR-48: Respects user consent preferences
 */
export function identifyUserInPostHog(user: any) {
  if (!user?.id) {
    console.warn('[PostHog] Cannot identify user: missing user.id', user)
    return
  }

  // FR-48: Check user consent first
  const { analyticsConsent } = useConsentStore.getState()
  
  if (analyticsConsent === false) {
    posthog.opt_out_capturing()
    posthog.set_config({ autocapture: false, capture_pageview: false })
    posthog.stopSessionRecording()
    return
  }

  // FR-08: Check if PostHog is enabled for this organization
  const posthogEnabled = user?.companyMember?.company?.posthogEnabled ?? true
  
  // FR-10: Check if mobile tracking is disabled for mobile users
  const isMobile = isMobileDevice()
  const posthogMobileEnabled = user?.companyMember?.company?.posthogMobileEnabled ?? true
  
  // Determine if tracking should be enabled
  // analyticsConsent can be true, false, or null (not asked yet)
  const shouldTrack = posthogEnabled && (!isMobile || posthogMobileEnabled) && (analyticsConsent === true || analyticsConsent === null)
  
  if (!shouldTrack) {
    // Disable all tracking including autocapture and session recording
    posthog.opt_out_capturing()
    posthog.set_config({ autocapture: false, capture_pageview: false })
    posthog.stopSessionRecording()
    return
  }

  // Enable autocapture and pageview tracking
  posthog.set_config({ autocapture: true, capture_pageview: true })
  
  // Enable PostHog tracking (must be called BEFORE identify to capture identify event)
  posthog.opt_in_capturing()
  
  // Enable session recording if PostHog is enabled
  posthog.startSessionRecording()

  // Determine user role (FR-05)
  let role = 'employee'
  if (user.companyMember?.isAdmin || user.companyMember?.isOwner) {
    role = 'hr'
  } else if (user.vendorMember) {
    role = 'vendor'
  }

  // FR-05: User identification with required properties
  // FR-43: Include is_mobile in user properties
  // FR-49: No PII - using userId instead of email/firstName/lastName
  const userProperties = {
    userId: user.id,
    role: role,
    organisation_id: user.companyMember?.company.slug || user.vendorMember?.vendor.slug || '',
    articles_read_count: user.articlesReadCount || 0,
    days_since_registration: user.daysSinceRegistration,
    is_mobile: isMobileDevice(),
    companyMember: !!user.companyMember,
    vendorMember: !!user.vendorMember,
  }
  
  posthog.identify(user.id, userProperties)

  // FR-06: Organization group identification
  if (user.companyMember?.company) {
    const company = user.companyMember.company
    const groupProperties = {
      name: company.name,
      type: company.accountType,
      employee_count: company.seatsInUse || 0,
      total_seats: company.totalSeats || 0,
    }
    posthog.group('organisation', company.slug, groupProperties)
  } else if (user.vendorMember?.vendor) {
    const vendor = user.vendorMember.vendor
    const groupProperties = {
      name: vendor.name,
      type: 'vendor',
    }
    posthog.group('organisation', vendor.slug, groupProperties)
  }
}
